import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronUp, Menu, X, ChevronRight, ChevronDown } from 'lucide-react';
import logo from '../assets/logo.png';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  const services = [
    { name: 'AI & DS', path: '/services/ai-ds' },
    { name: 'GenAI', path: '/services/genai' },
    { name: 'Edge AI', path: '/services/edge-ai' },
    { name: 'Devops', path: '/services/devops' },
    { name: 'Cybersecurity', path: '/services/cybersecurity' },
  ];

  // Prevent scrolling when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpen]);

  return (
    <nav className="bg-brand-card border-b border-brand-bg sticky top-0 z-50">
      <div className="w-full px-10 h-24 md:h-28 flex justify-between items-center bg-brand-card relative z-50">
        {/* Logo Section */}
        <div className="flex-shrink-0 flex items-center">
          <Link to="/">
            <img src={logo} alt="Sutherland" className="h-16 md:h-24 w-auto object-contain py-1" />
          </Link>
        </div>

        {/* Navigation Links - Hidden on small mobile, visible on desktop */}
        <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
          <div
            className="relative"
            onMouseEnter={() => setActiveDropdown('whatWeDo')}
            onMouseLeave={() => setActiveDropdown(null)}
          >
            <div className="flex items-center space-x-1 cursor-pointer text-brand-textLight hover:text-brand-gold font-medium text-sm xl:text-base py-4">
              <span>What We Do</span>
              <ChevronDown size={16} strokeWidth={2.5} className={`transition-transform duration-200 ${activeDropdown === 'whatWeDo' ? 'rotate-180' : ''}`} />
            </div>

            {/* Dropdown Menu */}
            {activeDropdown === 'whatWeDo' && (
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-max bg-brand-card border border-gray-800 shadow-xl rounded-lg py-4 px-2 z-50 animate-in fade-in zoom-in-95 duration-100">
                <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-brand-card border-t border-l border-gray-800 rotate-45"></div>
                <div className="flex flex-row items-center gap-2">
                  <ul className="flex flex-row space-x-2">
                    {services.map((service, index) => (
                      <li key={index}>
                        <Link
                          to={service.path}
                          className="block px-4 py-2 text-sm text-brand-textLight hover:bg-brand-bg hover:text-brand-gold transition-colors rounded-md whitespace-nowrap"
                        >
                          {service.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
          <Link to="/solutions" className="text-brand-textLight hover:text-brand-gold font-medium text-sm xl:text-base">
            Solutions
          </Link>
          <Link to="/skill-to-deploy" className="text-brand-textLight hover:text-brand-gold font-medium text-sm xl:text-base">
            Skill to Deploy
          </Link>
          <Link to="/career-chavdi" className="text-brand-textLight hover:text-brand-gold font-medium text-sm xl:text-base">
            Career Chavdi
          </Link>
          <Link to="/about-us" className="text-brand-textLight hover:text-brand-gold font-medium text-sm xl:text-base">
            About Us
          </Link>
          <Link to="/careers" className="text-brand-textLight hover:text-brand-gold font-medium text-sm xl:text-base">
            Careers
          </Link>
        </div>

        {/* Right Actions */}
        <div className="flex items-center space-x-4">
          <Link
            to="/contact-us"
            className="hidden md:inline-block bg-brand-gold hover:bg-brand-goldHover text-black font-bold py-2 px-6 rounded shadow-sm transition-colors text-sm uppercase tracking-wide"
          >
            Contact Us
          </Link>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center">
            <button
              onClick={() => setIsOpen(true)}
              className="text-brand-textLight hover:text-brand-gold focus:outline-none p-2"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div className={`fixed inset-0 z-[60] lg:hidden ${isOpen ? 'visible' : 'invisible'}`}>
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setIsOpen(false)}
        />

        {/* Drawer */}
        <div className={`absolute top-0 right-0 w-[85%] max-w-sm h-full bg-brand-card shadow-xl transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex flex-col h-full bg-brand-card">
            {/* Drawer Header */}
            <div className="p-6 flex justify-between items-start">
              <Link
                to="/contact-us"
                className="bg-brand-gold hover:bg-brand-goldHover text-black font-bold py-3 px-6 rounded shadow-sm transition-colors text-sm uppercase tracking-wide"
                onClick={() => setIsOpen(false)}
              >
                Contact Us
              </Link>

              <button
                onClick={() => setIsOpen(false)}
                className="text-brand-gold hover:text-brand-goldHover focus:outline-none p-1"
              >
                <X size={32} strokeWidth={2.5} />
              </button>
            </div>

            {/* Drawer Links */}
            <div className="flex-1 overflow-y-auto px-6 py-2">
              <ul className="space-y-0">
                <li className="border-b border-gray-800">
                  <div className="flex flex-col">
                    <button
                      className="flex justify-between items-center py-4 text-brand-textLight hover:text-brand-gold font-medium text-lg w-full text-left focus:outline-none"
                      onClick={() => setActiveDropdown(activeDropdown === 'mobileWhatWeDo' ? null : 'mobileWhatWeDo')}
                    >
                      What We Do
                      <ChevronDown size={20} className={`transform transition-transform duration-200 ${activeDropdown === 'mobileWhatWeDo' ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Mobile Submenu */}
                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${activeDropdown === 'mobileWhatWeDo' ? 'max-h-[500px] opacity-100 mb-4' : 'max-h-0 opacity-0'}`}>
                      <ul className="space-y-2 pl-4 border-l-2 border-gray-800 ml-2">
                        {services.map((service, index) => (
                          <li key={index}>
                            <Link
                              to={service.path}
                              className="block py-2 text-brand-textLight hover:text-brand-gold text-base"
                              onClick={() => setIsOpen(false)}
                            >
                              {service.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </li>
                <li className="border-b border-gray-800">
                  <Link
                    to="/solutions"
                    className="flex justify-between items-center py-4 text-brand-textLight hover:text-brand-gold font-medium text-lg"
                    onClick={() => setIsOpen(false)}
                  >
                    Solutions
                    <ChevronRight size={20} />
                  </Link>
                </li>
                <li className="border-b border-gray-800">
                  <Link
                    to="/skill-to-deploy"
                    className="flex justify-between items-center py-4 text-brand-textLight hover:text-brand-gold font-medium text-lg"
                    onClick={() => setIsOpen(false)}
                  >
                    Skill to Deploy
                    <ChevronRight size={20} />
                  </Link>
                </li>
                <li className="border-b border-gray-800">
                  <Link
                    to="/career-chavdi"
                    className="flex justify-between items-center py-4 text-brand-textLight hover:text-brand-gold font-medium text-lg"
                    onClick={() => setIsOpen(false)}
                  >
                    Career Chavdi
                  </Link>
                </li>
                <li className="border-b border-gray-800">
                  <Link
                    to="/about-us"
                    className="flex justify-between items-center py-4 text-brand-textLight hover:text-brand-gold font-medium text-lg"
                    onClick={() => setIsOpen(false)}
                  >
                    About Us
                    <ChevronRight size={20} />
                  </Link>
                </li>
                <li className="border-b border-gray-800">
                  <Link
                    to="/careers"
                    className="flex justify-between items-center py-4 text-brand-textLight hover:text-brand-gold font-medium text-lg"
                    onClick={() => setIsOpen(false)}
                  >
                    Careers
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Top right corner caret indicator */}
      <div className="hidden md:block absolute top-0 right-2 text-gray-400">
        <ChevronUp size={16} />
      </div>
    </nav>
  );
};

export default Navbar;
